/**
 * Backend Warm-up Utilities
 * Prevents Render cold starts with background health checks
 */

const BACKEND_URL = 'https://destiny-reborn.onrender.com';
const HEALTH_ENDPOINT = `${BACKEND_URL}/health`;

// Warm-up configuration
const WARMUP_CONFIG = {
  timeout: 8000,        // 8s timeout
  maxRetries: 2,        // Max retry attempts
  retryDelays: [200, 500], // Backoff delays in ms
  logPrefix: '[Warmup]'
};

/**
 * Immediate warm-up call (fire and forget)
 * Call this on page load to pre-warm backend
 */
export function triggerImmediateWarmup() {
  // Fire immediately without blocking UI
  setTimeout(async () => {
    try {
      console.log(`${WARMUP_CONFIG.logPrefix} Starting immediate backend warmup...`);
      const result = await performHealthCheck();
      console.log(`${WARMUP_CONFIG.logPrefix} Warmup completed:`, result.status, `(${result.responseTime}ms)`);
    } catch (error) {
      console.warn(`${WARMUP_CONFIG.logPrefix} Warmup failed:`, error.message);
    }
  }, 0);
}

/**
 * Enhanced health check with retries
 * Used for critical operations like login
 */
export async function performHealthCheckWithRetries() {
  let lastError = null;
  
  for (let attempt = 0; attempt <= WARMUP_CONFIG.maxRetries; attempt++) {
    try {
      // Add delay for retries
      if (attempt > 0) {
        const delay = WARMUP_CONFIG.retryDelays[attempt - 1] || 500;
        console.log(`${WARMUP_CONFIG.logPrefix} Retry ${attempt} in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      
      const result = await performHealthCheck();
      
      if (result.status === 'ok') {
        if (attempt > 0) {
          console.log(`${WARMUP_CONFIG.logPrefix} Success on retry ${attempt}`);
        }
        return result;
      } else {
        throw new Error(`Health check failed: ${result.error}`);
      }
      
    } catch (error) {
      lastError = error;
      console.warn(`${WARMUP_CONFIG.logPrefix} Attempt ${attempt + 1} failed:`, error.message);
      
      // If last attempt, throw error
      if (attempt === WARMUP_CONFIG.maxRetries) {
        throw lastError;
      }
    }
  }
}

/**
 * Core health check function
 * Returns standardized result object
 */
async function performHealthCheck() {
  const startTime = Date.now();
  
  try {
    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), WARMUP_CONFIG.timeout);
    
    const response = await fetch(HEALTH_ENDPOINT, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      },
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    const responseTime = Date.now() - startTime;
    
    if (response.ok) {
      const data = await response.json().catch(() => ({}));
      
      return {
        status: 'ok',
        httpStatus: response.status,
        responseTime,
        data,
        timestamp: Date.now()
      };
    } else {
      return {
        status: 'error',
        httpStatus: response.status,
        responseTime,
        error: `HTTP ${response.status}`,
        timestamp: Date.now()
      };
    }
    
  } catch (error) {
    const responseTime = Date.now() - startTime;
    const errorMessage = error.name === 'AbortError' ? 'Timeout' : error.message;
    
    return {
      status: 'error',
      responseTime,
      error: errorMessage,
      timestamp: Date.now()
    };
  }
}

/**
 * Pre-warm before critical operations
 * Use before login, registration, etc.
 */
export async function prewarmForCriticalOperation() {
  console.log(`${WARMUP_CONFIG.logPrefix} Pre-warming for critical operation...`);
  
  try {
    const result = await performHealthCheckWithRetries();
    console.log(`${WARMUP_CONFIG.logPrefix} Pre-warm successful (${result.responseTime}ms)`);
    return true;
  } catch (error) {
    console.warn(`${WARMUP_CONFIG.logPrefix} Pre-warm failed, continuing anyway:`, error.message);
    return false;
  }
}

/**
 * Check if backend is likely cold (slow response)
 * Helps decide when to show loading states
 */
export function isLikelyColdStart(responseTime) {
  return responseTime > 3000; // 3+ seconds indicates cold start
}

// Auto-trigger warmup on module load (only in browser)
if (typeof window !== 'undefined') {
  // Trigger warmup after a short delay to not block initial page load
  setTimeout(triggerImmediateWarmup, 1000);
}